/**
 * Global toast state — shared across UI versions.
 * Each version mounts its own `ToastRoot` UI; the store is single-process global.
 */
import { create } from 'zustand';

/** Visible time before dismiss animation (excludes 200ms enter/exit). */
export const DEFAULT_TOAST_DURATION_MS = 2000;

/** Default for `variant: 'error'` toasts (slightly longer to read the message). */
export const DEFAULT_ERROR_TOAST_DURATION_MS = 3000;

export type ToastVariant = 'default' | 'success' | 'error';

interface ToastState {
  message: string;
  visible: boolean;
  variant: ToastVariant;
  duration: number;
  show: (message: string, options?: { variant?: ToastVariant; duration?: number }) => void;
  hide: () => void;
}

export const useToastStore = create<ToastState>(set => ({
  message: '',
  visible: false,
  variant: 'default',
  duration: DEFAULT_TOAST_DURATION_MS,
  show: (message, options) =>
    set({
      message,
      visible: true,
      variant: options?.variant ?? 'default',
      duration: options?.duration ?? DEFAULT_TOAST_DURATION_MS,
    }),
  hide: () => set({ visible: false }),
}));

export function getToastStore(): ToastState {
  return useToastStore.getState();
}
